import type { ItemTreffer } from "@gui-creator/gemeinsam";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTexturStatus } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { ItemBild } from "../inventar/ItemBild.tsx";

const ID = /^[a-z0-9_.-]+:[a-z0-9_./-]+$/;
const GUELTIG = (s: string) => ID.test(s) || /\{[a-z0-9_]+\}/.test(s);

function useItemName(symbol: string) {
  const q = useQuery({
    queryKey: ["itemname", symbol],
    queryFn: () => api<ItemTreffer[]>(`/api/items?suche=${encodeURIComponent(symbol.replace(/^minecraft:/, ""))}`),
    enabled: ID.test(symbol),
    staleTime: Number.POSITIVE_INFINITY,
  });
  return q.data?.find((t) => t.id === symbol)?.name ?? null;
}

/** Item-Suche nach deutschem oder englischem Namen und ID; freie IDs und Platzhalter gehen immer. */
export function ItemWahl({ label, wert, onWert, deaktiviert, leer = "Item wählen" }: { label: string; wert: string | undefined; onWert: (id: string) => void; deaktiviert?: boolean; leer?: string }) {
  const id = useId();
  const knopf = useRef<HTMLButtonElement>(null);
  const [offen, setOffen] = useState<DOMRect | null>(null);
  const [suche, setSuche] = useState("");
  const [markiert, setMarkiert] = useState(0);
  const status = useTexturStatus();
  const name = useItemName(wert ?? "");
  const treffer = useQuery({
    queryKey: ["items", suche],
    queryFn: () => api<ItemTreffer[]>(`/api/items?suche=${encodeURIComponent(suche)}`),
    enabled: !!offen && suche.trim().length > 0 && !!status.data?.version,
  });
  const liste = treffer.data ?? [];
  const frei = suche.trim() && GUELTIG(suche.trim()) && !liste.some((t) => t.id === suche.trim()) ? suche.trim() : null;
  const eintraege = [...(frei ? [{ id: frei, name: "Eigene ID verwenden", nameEn: "" }] : []), ...liste];

  useEffect(() => setMarkiert(0), [suche]);

  const waehlen = (neu: string) => {
    onWert(neu);
    setOffen(null);
    setSuche("");
    knopf.current?.focus();
  };

  return (
    <div className="feld">
      <span className="feld-titel" id={`${id}-l`}>
        {label}
      </span>
      <button
        ref={knopf}
        type="button"
        className="itemwahl"
        aria-labelledby={`${id}-l`}
        aria-haspopup="listbox"
        aria-expanded={!!offen}
        disabled={deaktiviert}
        onClick={() => setOffen(knopf.current!.getBoundingClientRect())}
      >
        {wert ? <ItemBild symbol={wert} groesse={20} /> : <span className="itemwahl-leer" />}
        <span className="itemwahl-text">
          <span>{wert ? (name ?? wert.replace(/^minecraft:/, "")) : leer}</span>
          {wert && name && <span className="mono leise">{wert}</span>}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {offen &&
        createPortal(
          <ItemSuche
            rahmen={offen}
            suche={suche}
            setSuche={setSuche}
            eintraege={eintraege}
            markiert={markiert}
            setMarkiert={setMarkiert}
            ohneJar={!status.data?.version}
            laedt={treffer.isFetching}
            onWaehlen={waehlen}
            onSchliessen={() => {
              setOffen(null);
              knopf.current?.focus();
            }}
          />,
          document.body,
        )}
    </div>
  );
}

function ItemSuche(p: {
  rahmen: DOMRect;
  suche: string;
  setSuche: (s: string) => void;
  eintraege: ItemTreffer[];
  markiert: number;
  setMarkiert: (n: number) => void;
  ohneJar: boolean;
  laedt: boolean;
  onWaehlen: (id: string) => void;
  onSchliessen: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const daneben = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) p.onSchliessen();
    };
    document.addEventListener("pointerdown", daneben, true);
    return () => document.removeEventListener("pointerdown", daneben, true);
  }, [p]);
  const breite = Math.max(p.rahmen.width, 280);
  const links = Math.min(p.rahmen.left, window.innerWidth - breite - 8);
  return (
    <div ref={ref} className="itemsuche" style={{ left: Math.max(8, links), top: Math.min(p.rahmen.bottom + 4, window.innerHeight - 340), width: breite }}>
      <input
        className="eingabe"
        autoFocus
        placeholder={p.ohneJar ? "ID eingeben, z. B. minecraft:stone" : "Item suchen: Name oder ID"}
        aria-label="Item suchen"
        role="combobox"
        aria-expanded="true"
        aria-controls="itemsuche-liste"
        value={p.suche}
        onChange={(e) => p.setSuche(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            p.setMarkiert(Math.min(p.eintraege.length - 1, p.markiert + 1));
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            p.setMarkiert(Math.max(0, p.markiert - 1));
          }
          if (e.key === "Enter") {
            const t = p.eintraege[p.markiert];
            if (t) p.onWaehlen(t.id);
          }
          if (e.key === "Escape") p.onSchliessen();
        }}
      />
      <ul id="itemsuche-liste" role="listbox" className="itemsuche-liste">
        {p.eintraege.map((t, i) => (
          <li
            key={t.id}
            role="option"
            aria-selected={i === p.markiert}
            className={i === p.markiert ? "markiert" : ""}
            onPointerEnter={() => p.setMarkiert(i)}
            onClick={() => p.onWaehlen(t.id)}
          >
            <ItemBild symbol={t.id} groesse={20} />
            <span className="itemsuche-name">{t.name}</span>
            <span className="mono leise">{t.id.replace(/^minecraft:/, "")}</span>
          </li>
        ))}
      </ul>
      {p.suche.trim() && !p.laedt && p.eintraege.length === 0 && (
        <p className="itemsuche-leer">{p.ohneJar ? "Gültige ID im Format namespace:pfad eingeben." : "Kein Item gefunden. Eine eigene ID im Format namespace:pfad geht auch."}</p>
      )}
      {p.ohneJar && !p.suche && <p className="itemsuche-leer">Ohne Client-JAR gibt es keine Suche. Ein Admin kann sie unter Einstellungen hochladen.</p>}
    </div>
  );
}
